"use client";

import clsx from "clsx";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Company facts panel — sits beside the Seller Trust Profile on a public
 * catalogue page (screen 0.5 for Aurora, T3 discovery for Gulf Phosphate).
 *
 *   Trust profile  = network metrics (trades, on-time, disputes, …)
 *   Company facts = corporate identity (founded, HQ, size, certifications)
 *
 * Both are verifiable claims rendered as quiet, scannable rows.
 */
export type CompanyFact = {
  label: string;
  value: string;
};

export function CompanyFacts({
  title = "Company facts",
  facts,
  className,
}: {
  title?: string;
  facts: CompanyFact[];
  className?: string;
}) {
  return (
    <div className={clsx("glass p-4", className)}>
      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{title}</p>
      <ul className="mt-3 flex flex-col gap-2 text-[12px]">
        {facts.map((f) => (
          <li
            key={f.label}
            className="flex items-baseline justify-between gap-3 border-b border-state-verified/10 pb-1.5 last:border-b-0 last:pb-0"
          >
            <span className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{f.label}</span>
            <span className="text-right text-orbitra-paper">{f.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* Reusable factsets keyed by counterparty — extend as new sellers are added. */
export function useAuroraFacts(): CompanyFact[] {
  const t = useTranslations("companyFacts.aurora");
  return [
    { label: t("founded"),         value: "2008" },
    { label: t("headquarters"),    value: "Lucas do Rio Verde, Mato Grosso · BR" },
    { label: t("employees"),       value: "1,240" },
    { label: t("capacity"),        value: "48,000 t/mo frozen halal poultry" },
    { label: t("certifications"),  value: "ABCC · GIMDES Halal · BRCGS · ISO 22000" },
    { label: t("exportMarkets"),   value: "UAE · KSA · Egypt · Indonesia · Malaysia" },
    { label: t("vesselsLast12m"),  value: "87 vessels" },
    { label: t("lei"),             value: "9876-AURORA-BR" },
  ];
}

export function useGulfPhosphateFacts(): CompanyFact[] {
  const t = useTranslations("companyFacts.gulfPhosphate");
  return [
    { label: t("founded"),         value: "1994" },
    { label: t("headquarters"),    value: "Khalifa Industrial Zone, Abu Dhabi · UAE" },
    { label: t("employees"),       value: "2,180" },
    { label: t("capacity"),        value: "1.4 Mt/yr urea + 0.6 Mt/yr DAP" },
    { label: t("certifications"),  value: "ABCC · IFA · ISO 9001 · ISO 14001 · OHSAS 18001" },
    { label: t("exportMarkets"),   value: "Brazil · India · Bangladesh · South Africa" },
    { label: t("vesselsLast12m"),  value: "52 bulk-carrier sailings" },
    { label: t("lei"),             value: "5493-GULFPHOSPHATE-AE" },
  ];
}
